import React from 'react';

import { Form } from 'antd';
import styled, { css, keyframes } from 'styled-components';

import { normalizeValue } from 'utils/helpers/form';

import ShopDataModalAccomodationEditUnitModal from './ShopDataModalAccomodationEditUnitModal';
import CloseIcon from '../../../common/Icons/CloseIcon';
import Input from '../../lib/Input';
import InputNumber from '../../lib/InputNumber';
import { ACCOMMODATION_FIELDS } from '../constants';

const StyledCloseIcon = styled.div`
  display: flex;
  visibility: hidden;
  border-radius: 50%;
  cursor: pointer;
  align-items: center;
`;

const sortedHighlight = keyframes`
  0% {
    background-color: var(--background-light-blue);
  }

  100% {
    background-color: transparent;
  }
`;

const Container = styled.div<{ $highlightSort?: boolean }>`
  display: flex;
  gap: 10px;
  align-items: center;
  margin: -4px -8px;
  padding: 4px 8px;
  border-radius: 4px;

  ${({ $highlightSort }) =>
    $highlightSort &&
    css`
      animation: ${sortedHighlight} 900ms ease-out;
    `}

  &:hover {
    ${StyledCloseIcon} {
      visibility: visible;
    }
  }
`;

const Label = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 5px;
  width: 200px;

  font-size: 14px;
  white-space: nowrap;
  text-align: right;
`;

const Title = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: var(--font-bold);
  font-size: 14px;
`;
const Description = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: var(--font-regular);
  font-size: 12px;
  color: var(--dark-blue);
  cursor: pointer;
`;

interface Props {
  name: string[];
  label: string;
  description: string;
  showEntranceFee: boolean;
  onRemove?: (field: string) => void;
  onUpdateUnitType?: (newUnitType: string) => void;
  onBaseRentBlur?: () => void;
  onSquareFootageBlur?: () => void;
  highlightSort?: boolean;
  index?: number;
}

const ShopDataModalAccommodationEntry = ({
  name,
  label,
  description,
  showEntranceFee,
  onRemove,
  onUpdateUnitType,
  onBaseRentBlur,
  onSquareFootageBlur,
  highlightSort,
  index,
}: Props) => {
  const [showEditUnitModal, setShowEditUnitModal] = React.useState(false);
  const apartmentType = name[name.length - 1];
  const enabledRemoval = typeof onRemove === 'function';
  const form = Form.useFormInstance();

  return (
    <Container $highlightSort={highlightSort}>
      <Label>
        <Title data-test-id={`sd-modal-room-variant-${index}-label`}>{label}</Title>
        <Description
          onClick={() => setShowEditUnitModal(true)}
          data-test-id={`sd-modal-room-variant-${index}-unit-name`}
        >
          {description || 'Edit Unit Name'}
        </Description>
      </Label>
      <Form.Item noStyle name={[...name, 'id']} hidden />
      <Form.Item noStyle name={[...name, 'living_type_id']} hidden />
      <Form.Item noStyle name={[...name, 'living_type_abv']} hidden />
      <Form.Item noStyle name={[...name, 'apartment_type']} hidden />
      <Form.Item noStyle name={[...name, 'apartment_type_id']} hidden />
      <Form.Item noStyle name={[...name, 'apartment_type_id_slug']} hidden />
      <Form.Item noStyle name={[...name, 'apartment_type_group_id']} hidden />
      <Form.Item noStyle name={[...name, 'apartment_type_group_id_slug']} hidden />
      <Form.Item noStyle name={[...name, 'price_per_sq']} hidden />
      <Form.Item noStyle name={[...name, ACCOMMODATION_FIELDS.UNIT]} hidden />
      <Form.Item
        noStyle
        name={[...name, ACCOMMODATION_FIELDS.BASE_RENT]}
        rules={[{ type: 'number', min: 0 }, { required: true }]}
        normalize={normalizeValue()}
      >
        <InputNumber
          style={{ width: 120, height: 36 }}
          prefix={'$'}
          onBlur={onBaseRentBlur}
          data-test-id={`sd-modal-room-variant-${index}-monthly-fee-input`}
        />
      </Form.Item>
      {showEntranceFee && (
        <Form.Item noStyle name={[...name, ACCOMMODATION_FIELDS.ENTRANCE_FEE]} rules={[{ type: 'number', min: 0 }]}>
          <InputNumber
            style={{ width: 120, height: 36 }}
            prefix={'$'}
            data-test-id={`sd-modal-room-variant-${index}-entrance-fee-input`}
          />
        </Form.Item>
      )}
      <Form.Item noStyle name={[...name, ACCOMMODATION_FIELDS.SQUARE_FOOTAGE]} rules={[{ type: 'number', min: 1 }]}>
        <InputNumber
          style={{ width: 70, height: 36 }}
          precision={0}
          onBlur={onSquareFootageBlur}
          data-test-id={`sd-modal-room-variant-${index}-sqft-input`}
        />
      </Form.Item>
      <Form.Item style={{ flexGrow: 1 }} name={[...name, ACCOMMODATION_FIELDS.NOTES]}>
        <Input
          style={{ height: 36 }}
          placeholder={'Note (optional)'}
          data-test-id={`sd-modal-room-variant-${index}-note-input`}
        />
      </Form.Item>
      {enabledRemoval && (
        <StyledCloseIcon onClick={() => onRemove(apartmentType)}>
          <CloseIcon size={20} />
        </StyledCloseIcon>
      )}
      <ShopDataModalAccomodationEditUnitModal
        open={showEditUnitModal}
        roomType={label}
        unitName={description}
        onConfirm={(unit) => {
          form.setFieldValue([...name, ACCOMMODATION_FIELDS.UNIT], unit);
          if (onUpdateUnitType) onUpdateUnitType(unit);
          setShowEditUnitModal(false);
        }}
        onClose={() => setShowEditUnitModal(false)}
      ></ShopDataModalAccomodationEditUnitModal>
    </Container>
  );
};

export default ShopDataModalAccommodationEntry;
